/**
 * settings.ts — Player preferences that outlive a run.
 *
 * A sibling to `progress.ts`, and for the same reason: one small module owns one
 * storage key, so the rest of the game can ask for a setting without knowing
 * that `localStorage` exists or that it can fail.
 *
 * There is exactly one setting today — mute. It lived inside `SoundManager`
 * until cloud save needed to read and write it, and reaching into the audio
 * system to get at a boolean would have coupled the two for no good reason.
 *
 * The storage contract from `progress.ts` applies here as well: **every path
 * tolerates storage being unavailable.** A browser that refuses to remember the
 * setting gets the default back, never an exception.
 */

// Underscored rather than dotted like `dogwalker.completed`, because that is
// what SoundManager has been writing since sound landed. Renaming it would read
// as "we forgot you muted us" to everyone who already has, which is a worse
// outcome than an inconsistent key name.
const KEY = 'dogwalker_muted';

/** Whether sound is muted. `false` when unset or storage is unavailable. */
export function mutedSetting(): boolean {
  try {
    return window.localStorage.getItem(KEY) === 'true';
  } catch {
    return false;
  }
}

/** Remember the mute setting. Silently does nothing if storage is unavailable. */
export function setMutedSetting(muted: boolean): void {
  try {
    window.localStorage.setItem(KEY, String(muted));
  } catch {
    /* nothing worth doing: sound is still muted, it just won't be next time */
  }
}
